import {
  BookText,
  Boxes,
  ChartColumn,
  ClipboardList,
  Contact,
  LayoutDashboard,
  Package,
  ReceiptIndianRupee,
  Settings,
  ShoppingCart,
  Warehouse,
  type LucideIcon,
} from "lucide-react";

/**
 * One entry in a module's section column.
 *
 * `permission` is a key from the catalogue in §12 of the spec, shaped
 * `module.section.action`. M2-7 uses it to drop sections the user cannot
 * reach; M15-5 wires the real check. Hiding is never the enforcement — the
 * route handler checks regardless.
 */
export interface SectionDefinition {
  id: string;
  label: string;
  /** Appended to the module's path. Empty string means the module's index. */
  path: string;
  permission: string;
  /**
   * Permissions the section's data routes demand beyond `permission`.
   *
   * Absent for nearly every section. A section is shown only to someone
   * holding all of them, so it is never an entry that opens onto a refusal:
   * Reports > Orders is a Sales report (`reports.sales.view`), but its chart
   * counts orders, which `orders.list.view` guards.
   */
  requires?: string[];
  /**
   * A screen whose form does not save yet (#642). Left out of the sidebar so
   * nobody fills in a form that is gone on the next reload; the route still
   * answers, for whoever is building it.
   */
  unfinished?: boolean;
  /**
   * Reached from inside its module rather than the sidebar (#641): Docs'
   * Recent, Share (permissions) and Trash live in the docs tree. The route
   * still answers and its permission still applies.
   */
  inModuleOnly?: boolean;
}

/** Every permission a section needs: its own, then any it also requires. */
export function sectionPermissions(section: SectionDefinition): string[] {
  return [section.permission, ...(section.requires ?? [])];
}

/**
 * A top-level destination in the sidebar.
 *
 * A module has no permission of its own. It is visible when any of its
 * sections is, which is why the catalogue keys are `module.section.action`
 * and why there is no separate module-level key to keep in sync.
 */
export interface ModuleDefinition {
  id: string;
  label: string;
  icon: LucideIcon;
  path: string;
  /**
   * Phase 2 modules are declared but not built.
   *
   * Nothing is phase 2 any more — Docs moved to phase 1 on 2026-09-09, when
   * its screens were built against fixtures. The field stays because the
   * distinction will be wanted again, and a filter that already exists is
   * cheaper than one reintroduced under time pressure.
   */
  phase: 1 | 2;
  sections: SectionDefinition[];
}

/**
 * The navigation map from §11.2 of the spec, verbatim in content and order.
 * Catalog sits directly under Dashboard (2026-09-15): it is the first module
 * served from live data, and the one the team opens most.
 *
 * Eleven sidebar entries is more than most users see. It renders only what
 * someone holds a permission for, so a warehouse operator sees four.
 */
export const MODULES: ModuleDefinition[] = [
  {
    id: "dashboard",
    label: "Dashboard",
    icon: LayoutDashboard,
    path: "/",
    phase: 1,
    sections: [
      {
        id: "overview",
        label: "Overview",
        path: "",
        permission: "dashboard.overview.view",
      },
    ],
  },
  {
    id: "catalog",
    label: "Catalog",
    icon: Package,
    path: "/catalog",
    phase: 1,
    sections: [
      {
        id: "products",
        label: "Products",
        path: "",
        permission: "catalog.product.view",
      },
      {
        id: "hsn",
        label: "HSN Master",
        path: "/hsn",
        permission: "catalog.hsn.view",
      },
      {
        id: "missing-hsn",
        label: "Missing HSN",
        path: "/missing-hsn",
        permission: "catalog.product.view",
      },
    ],
  },
  {
    id: "orders",
    label: "Orders",
    icon: ShoppingCart,
    path: "/orders",
    phase: 1,
    sections: [
      {
        id: "all",
        label: "All Orders",
        path: "",
        permission: "orders.list.view",
      },
      {
        id: "unfulfilled",
        label: "Unfulfilled",
        path: "/unfulfilled",
        permission: "orders.list.view",
      },
      {
        id: "shipments",
        label: "Shipments",
        path: "/shipments",
        permission: "orders.shipments.view",
      },
      {
        // Parcels that failed delivery or are coming back, to be received by
        // the warehouse (#529). The photos of damaged items are read with
        // `orders.detail.view`, so the section needs it too.
        id: "returns",
        label: "Returns to receive",
        path: "/returns",
        permission: "orders.returns.receive",
        requires: ["orders.detail.view"],
      },
      {
        // Parcels that failed delivery, went quiet or were lost (#702). The
        // order page opens from it, so it needs `orders.detail.view` too.
        id: "escalations",
        label: "Escalations",
        path: "/escalations",
        permission: "orders.escalations.view",
        requires: ["orders.detail.view"],
      },
      {
        id: "changes",
        label: "Recent Changes",
        path: "/changes",
        permission: "orders.history.view",
      },
    ],
  },
  {
    id: "inventory",
    label: "Inventory",
    icon: Boxes,
    path: "/inventory",
    phase: 1,
    sections: [
      {
        id: "levels",
        label: "Stock Levels",
        path: "",
        permission: "inventory.levels.view",
      },
      {
        id: "update-stock",
        label: "Update stock",
        path: "/update-stock",
        permission: "inventory.adjustment.create",
      },
    ],
  },
  {
    id: "warehouses",
    label: "Warehouses",
    icon: Warehouse,
    path: "/warehouses",
    phase: 1,
    sections: [
      {
        id: "list",
        label: "Warehouses",
        path: "",
        permission: "warehouse.list.view",
      },
      {
        id: "transfers",
        label: "Transfers",
        path: "/transfers",
        permission: "warehouse.transfer.view",
      },
      {
        id: "in-transit",
        label: "In Transit",
        path: "/in-transit",
        permission: "warehouse.transfer.view",
      },
    ],
  },
  {
    id: "contacts",
    label: "Contacts",
    icon: Contact,
    path: "/contacts",
    phase: 1,
    sections: [
      {
        id: "all",
        label: "All Contacts",
        path: "",
        permission: "contacts.contact.view",
      },
      {
        id: "vendors",
        label: "Vendors",
        path: "/vendors",
        permission: "contacts.contact.view",
      },
      {
        id: "customers",
        label: "Customers",
        path: "/customers",
        // Shopify's customers (#447), not the Customer contact type — which
        // is still there under All Contacts, filtered by type.
        permission: "customers.view",
      },
      {
        id: "suppliers",
        label: "Suppliers",
        path: "/suppliers",
        permission: "contacts.contact.view",
      },
      {
        id: "types",
        label: "Contact Types",
        path: "/types",
        permission: "contacts.type.manage",
      },
    ],
  },
  {
    id: "gst",
    label: "GST",
    icon: ReceiptIndianRupee,
    path: "/gst",
    phase: 1,
    sections: [
      {
        id: "invoices",
        label: "Invoices",
        path: "",
        permission: "gst.invoice.view",
      },
      {
        id: "credit-notes",
        label: "Credit Notes",
        path: "/credit-notes",
        permission: "gst.invoice.view",
      },
      {
        id: "hsn-summary",
        label: "HSN Summary",
        path: "/hsn-summary",
        permission: "gst.export.run",
      },
      {
        id: "exports",
        label: "Exports",
        path: "/exports",
        permission: "gst.export.run",
      },
      {
        id: "settings",
        label: "GST Settings",
        path: "/settings",
        permission: "gst.settings.manage",
        unfinished: true,
      },
    ],
  },
  {
    id: "tasks",
    label: "Tasks",
    icon: ClipboardList,
    path: "/tasks",
    phase: 1,
    sections: [
      {
        id: "mine",
        label: "My Tasks",
        path: "",
        permission: "tasks.task.view",
      },
      {
        id: "board",
        label: "Board",
        path: "/board",
        permission: "tasks.task.view",
      },
      {
        id: "all",
        label: "All Tasks",
        path: "/all",
        permission: "tasks.task.view",
      },
      {
        id: "recurring",
        label: "Recurring",
        path: "/recurring",
        permission: "tasks.task.view",
      },
      {
        id: "projects",
        label: "Projects",
        path: "/projects",
        permission: "tasks.project.manage",
      },
    ],
  },
  {
    id: "docs",
    label: "Docs",
    icon: BookText,
    path: "/docs",
    phase: 1,
    sections: [
      {
        id: "browse",
        label: "Browse",
        path: "",
        permission: "docs.page.view",
      },
      {
        id: "recent",
        inModuleOnly: true,
        label: "Recent",
        path: "/recent",
        permission: "docs.page.view",
      },
      {
        id: "search",
        // Global search (#640) finds docs now; this stays for its snippets.
        inModuleOnly: true,
        label: "Search",
        path: "/search",
        permission: "docs.page.view",
      },
      {
        id: "permissions",
        inModuleOnly: true,
        label: "Share",
        path: "/permissions",
        permission: "docs.permissions.manage",
      },
      {
        id: "deleted",
        inModuleOnly: true,
        label: "Trash",
        path: "/deleted",
        // The same catalogue permission `DELETE /pages/:id` itself requires
        // (`docs.page.delete`) — folder-level Manage, not this, is what
        // actually decides which folder's deleted pages show up (FR-DOC-13).
        permission: "docs.page.delete",
      },
    ],
  },
  {
    id: "reports",
    label: "Reports",
    icon: ChartColumn,
    path: "/reports",
    phase: 1,
    sections: [
      {
        id: "sales",
        label: "Sales",
        path: "",
        permission: "reports.sales.view",
      },
      {
        // The line chart of orders over time (#449) — a Sales report, so it
        // is the same permission rather than a new key to seed.
        id: "orders",
        label: "Orders",
        path: "/orders",
        permission: "reports.sales.view",
        // The series route needs both: its filter can name a customer, and a
        // count over that is a read of what the orders list guards.
        requires: ["orders.list.view"],
      },
      {
        // RTO rate, delivery success, first-attempt rate and speeds (#528).
        // Counts shipments, so it needs what reading them needs.
        id: "delivery",
        label: "Delivery",
        path: "/delivery",
        permission: "reports.sales.view",
        requires: ["orders.shipments.view"],
      },
      {
        id: "products",
        label: "Products",
        path: "/products",
        permission: "reports.products.view",
      },
      {
        id: "inventory",
        label: "Inventory",
        path: "/inventory",
        permission: "reports.inventory.view",
      },
      {
        id: "gst",
        label: "GST",
        path: "/gst",
        permission: "reports.gst.view",
      },
      {
        // Damaged and missing returns (#529), with their photos, so it needs
        // what reading a photo needs.
        id: "returns",
        label: "Damaged returns",
        path: "/returns",
        permission: "orders.returns.receive",
        requires: ["orders.detail.view"],
      },
    ],
  },
  {
    id: "settings",
    label: "Settings",
    icon: Settings,
    path: "/settings",
    phase: 1,
    sections: [
      {
        id: "users",
        label: "Users",
        path: "",
        permission: "settings.users.manage",
      },
      {
        id: "groups",
        label: "Groups",
        path: "/groups",
        permission: "settings.groups.manage",
      },
      {
        id: "roles",
        label: "Roles & Permissions",
        path: "/roles",
        permission: "settings.roles.manage",
      },
      {
        id: "company",
        label: "Company",
        path: "/company",
        permission: "settings.company.manage",
        unfinished: true,
      },
      {
        id: "views",
        label: "Saved Views",
        path: "/views",
        permission: "settings.views.manage",
      },
      {
        id: "sync",
        label: "Shopify Sync",
        path: "/sync",
        permission: "settings.sync.view",
      },
      {
        id: "logistics",
        label: "Logistics partners",
        path: "/logistics",
        permission: "settings.sync.view",
      },
      {
        // Who the refund task of a prepaid return goes to (#529). Under the
        // same permission the tracking settings are saved with.
        id: "returns",
        label: "Returns",
        path: "/returns",
        permission: "settings.company.manage",
      },
      {
        // The morning summary's time and the global quiet hours (#446). Under
        // the permission the other company-wide settings are saved with.
        id: "notifications",
        label: "Notifications",
        path: "/notifications",
        permission: "settings.company.manage",
      },
      {
        // Where the delivery escalation emails go and the two status email
        // times (#702).
        id: "escalation-emails",
        label: "Escalation emails",
        path: "/escalation-emails",
        permission: "orders.escalations.settings",
      },
      {
        // Whether phone and desktop push is set up, and what failed (#446).
        // Under the permission the notification settings are changed with.
        id: "push",
        label: "Push",
        path: "/push",
        permission: "settings.company.manage",
      },
      {
        // Which tag means what to an order's stage (#465). Under the permission
        // that already governs the shared tag vocabulary.
        id: "order-stages",
        label: "Order stages",
        path: "/order-stages",
        permission: "settings.tags.manage",
      },
      {
        // The amber and red limits the dashboard colours its numbers by (#528).
        id: "dashboard",
        label: "Dashboard",
        path: "/dashboard",
        permission: "settings.company.manage",
      },
      {
        id: "tags",
        label: "Tags",
        path: "/tags",
        permission: "settings.tags.manage",
      },
      {
        id: "audit",
        label: "Audit Log",
        path: "/audit",
        permission: "settings.audit.view",
      },
    ],
  },
];

/** The modules the sidebar offers today. Docs is declared but not built. */
export const SHIPPED_MODULES = MODULES.filter((m) => m.phase === 1);

/**
 * `strict` plus `noUncheckedIndexedAccess` means `array[0]` is `T | undefined`,
 * which is correct and inconvenient for lists that are constants a few lines
 * up. These helpers state the invariant once, loudly, instead of scattering
 * non-null assertions through the shell.
 */
function requireFirst<T>(items: T[], what: string): T {
  const first = items[0];
  if (!first) throw new Error(`${what} is empty`);
  return first;
}

export const DEFAULT_MODULE = requireFirst(SHIPPED_MODULES, "SHIPPED_MODULES");

export function findModule(id: string): ModuleDefinition {
  return SHIPPED_MODULES.find((m) => m.id === id) ?? DEFAULT_MODULE;
}

export function findSection(
  module: ModuleDefinition,
  id: string,
): SectionDefinition {
  return (
    module.sections.find((s) => s.id === id) ??
    requireFirst(module.sections, `${module.id} sections`)
  );
}

export function firstSection(module: ModuleDefinition): SectionDefinition {
  return requireFirst(module.sections, `${module.id} sections`);
}
